// Scoring & metrics utilities

// OCE v2 - On-Court Efficiency (Version 2.0 - November 2025)
// Fairer Impact-Faktor bei Blowouts, bessere Freiwurf-Bewertung, Aggressivitäts-Bonus
export const calcOCEFromGame = (g, teamMargin = null) => {
  const GAME_MINUTES = 40; // Standard Basketball Spielzeit
  
  // Parse minutes - handle both "15:17" format and plain numbers
  let minutes = 0;
  if (typeof g.minutes === 'string' && g.minutes.includes(':')) {
    // Format "MM:SS" - convert to decimal minutes
    const [min, sec] = g.minutes.split(':').map(Number);
    minutes = min + (sec / 60);
  } else {
    minutes = Number(g.minutes || 0);
  }
  minutes = Math.max(1, minutes); // Minimum 1 to avoid division by zero
  
  const points = Number(g.points || 0);
  const plusMinus = Number(g.plus_minus || 0);
  const fouls = Number(g.fouls || 0);
  const ftMade = Number(g.free_throws_made || 0);
  const ftAtt = Number(g.free_throws_att || 0);
  
  // PPM - Points per Minute
  const PPM = points / minutes;
  
  // IMPACT - Fairer Team-Impact-Faktor
  let IMPACT = 1;
  if (teamMargin !== null && teamMargin !== undefined) {
    // Erwarteter Plus/Minus basierend auf Team-Performance
    const ExpectedPerMin = teamMargin / GAME_MINUTES;
    const ExpectedPlusMinus = ExpectedPerMin * minutes;
    const RelativePlusMinus = plusMinus - ExpectedPlusMinus;
    
    // Presence-Faktor: Je mehr Spielzeit, desto stärker wirkt der relative Impact
    const Presence = minutes / GAME_MINUTES;
    const PresenceMultiplier = 1 + 0.2 * Presence;
    
    IMPACT = 1 + (RelativePlusMinus / 10) * PresenceMultiplier;
  } else {
    // Fallback auf alte Formel wenn teamMargin nicht verfügbar
    IMPACT = 1 + plusMinus / 10;
  }
  
  // FT_adj - Freiwurf-Faktor (70% ist neutral)
  let FT_adj = 1.0;
  if (ftAtt > 0) {
    const ftPercent = ftMade / ftAtt;
    FT_adj = 1 + 1 * (ftPercent - 0.70);
    FT_adj = Math.max(0.80, Math.min(1.20, FT_adj)); // Clamp zwischen 0.80 und 1.20
  }
  // Wenn keine Freiwürfe: FT_adj = 1.0 (neutral)
  
  // FoulAgg - Aggressivitäts-Multiplikator
  const Fouls_expected = (fouls / minutes) * GAME_MINUTES;
  const Fouls_round = Math.round(Fouls_expected);
  
  let FoulAgg = 1.0;
  if (Fouls_round === 0) {
    FoulAgg = 0.95; // Zu passiv
  } else if (Fouls_round === 1) {
    FoulAgg = 0.98; // Wenig Präsenz
  } else if (Fouls_round >= 2 && Fouls_round <= 3) {
    FoulAgg = 1.03; // Optimal
  } else { // 4+
    FoulAgg = 1.10; // Maximal aggressiv
  }
  
  // OCE v2 - Endformel
  const OCE = (PPM * 10) * IMPACT * FT_adj * FoulAgg;
  
  return { 
    PPM, 
    IMPACT, 
    FT_adj, 
    FoulAgg,
    OCE,
    // Legacy für Kompatibilität
    FoulFactor: fouls === 0 ? 0 : fouls >= 4 ? 1 : 0.5
  };
};

export const calcKCE = ({ minutes = 0, beers = 0, responsibility_points = 0 }) => {
  // KCE = (Minuten / 15) + (Biere × 0.9) + (Zurechnungsfähigkeitspunkte × 4)
  // Zurechnungsfähigkeitspunkte: 1 Punkt = 60 Min wert (manueller Checkout = verantwortungsvoll)
  const KCE = Number(minutes) / 15 + Number(beers) * 0.9 + Number(responsibility_points) * 4;
  return { KCE };
};

export const scoreDistance = (pred, real) => Math.abs(pred.h - real.h) + Math.abs(pred.a - real.a);

export const computeBetPoints = (pred, real) => {
  let pts = 0;
  const predWinner = pred.h === pred.a ? 0 : pred.h > pred.a ? 1 : -1;
  const realWinner = real.h === real.a ? 0 : real.h > real.a ? 1 : -1;
  if (predWinner === realWinner) pts += 5; // Sieger korrekt
  const dist = scoreDistance(pred, real);
  pts += Math.max(0, 10 - dist); // -1 pro Differenz, min 0 (Basis 10)
  if (real.t === 0) {
    if (pred.t === 0) pts += 1; // cap wenn tatsächliche T=0
  } else {
    if (pred.t === real.t) pts += 3;
  }
  return Math.max(0, Math.floor(pts));
};
